import {
  ChartAggOption,
  SmoothingType,
  type XAxisFormatValues,
} from '../../../../generated/graphql';
import {
  type DerivedPointVisualizationOption,
  type LinePlotSettings,
  POINT_VISUALIZATION_OPTIONS,
  TooltipNumberOfRunsOptions,
  XAxisValues,
} from '../types';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from './maxRunDefaults';

export const xAxisLabels: {[key: string]: string} = {
  [XAxisValues.Step]: 'Step',
  [XAxisValues.RunTime]: 'Relative Time (Process)',
  [XAxisValues.AbsoluteRunTime]: 'Relative Time (Wall)',
  [XAxisValues.Timestamp]: 'Wall Time',
};

export const DEFAULT_ORGANZED_SETTINGS = {};

// x-axis
export const DEFAULT_X_AXIS_OPTION = {
  value: XAxisValues.Step,
  label: xAxisLabels[XAxisValues.Step],
};
export const DEFAULT_X_AXIS_SETTINGS = {
  xAxis: XAxisValues.Step,
  xAxisMin: undefined,
  xAxisMax: undefined,
  xLogScale: undefined,
  xAxisFormat: undefined,
};
export const DEFAULT_X_AXIS_OPTION_VALUES: string[] = [
  XAxisValues.RunTime,
  XAxisValues.AbsoluteRunTime,
  XAxisValues.Timestamp,
  XAxisValues.Step,
];

// smoothing
export const DEFAULT_SMOOTHING_SETTINGS: {
  smoothingWeight: number;
  smoothingType: SmoothingType;
} = {
  smoothingWeight: 0,
  smoothingType: SmoothingType.ExponentialTimeWeighted,
};

export const EMPTY_SETTINGS = {};

type DefaultLinePlotSettings = Required<
  Pick<
    LinePlotSettings,
    | 'colorRunNames'
    | 'displayFullRunName'
    | 'highlightedCompanionRunOnly'
    | 'groupAgg'
    | 'limit'
    | 'showXRange'
    | 'smoothingType'
    | 'smoothingWeight'
    | 'tooltipNumberOfRuns'
    | 'useRunsTableGroupingInPanels'
    | 'xAxis'
  > & {
    coordinateZooming: boolean;
    groupingDepth: number | undefined;
    pointVisualizationMethod: DerivedPointVisualizationOption;
    showLegend: boolean | undefined;
    xAxisFormat: XAxisFormatValues | undefined;
    xAxisMin: number | undefined;
    xAxisMax: number | undefined;
    xLogScale: boolean;
    yAxisMin: number | undefined;
    yAxisMax: number | undefined;
    yLogScale: boolean;
    showOriginalAfterSmoothing: boolean;
    windowing: boolean;
  }
>;
export const DEFAULT_LINE_PLOT_SETTINGS: DefaultLinePlotSettings = {
  colorRunNames: true,
  coordinateZooming: false,
  displayFullRunName: false,
  groupAgg: ChartAggOption.Mean,
  groupingDepth: undefined,
  highlightedCompanionRunOnly: false,
  limit: DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
  pointVisualizationMethod: POINT_VISUALIZATION_OPTIONS.Sampling,
  showLegend: undefined,
  showOriginalAfterSmoothing: true,
  showXRange: false,
  smoothingType: DEFAULT_SMOOTHING_SETTINGS.smoothingType,
  smoothingWeight: DEFAULT_SMOOTHING_SETTINGS.smoothingWeight,
  tooltipNumberOfRuns: TooltipNumberOfRunsOptions.Default,
  useRunsTableGroupingInPanels: true,
  xAxis: DEFAULT_X_AXIS_SETTINGS.xAxis,
  xAxisFormat: undefined,
  xAxisMax: undefined,
  xAxisMin: undefined,
  xLogScale: false,
  yAxisMax: undefined,
  yAxisMin: undefined,
  yLogScale: false,
  windowing: true,
};
export type DefaultLinePlotSettingKeys =
  keyof typeof DEFAULT_LINE_PLOT_SETTINGS;

/**
 * If currValue is null and defaultSettingType is defined, the defaultValue will be applied.
 */
export const getLinePlotSettingWithDefault = <
  T extends keyof DefaultLinePlotSettings,
>(
  currValue: DefaultLinePlotSettings[T] | undefined,
  defaultSettingType: T
): DefaultLinePlotSettings[T] => {
  return currValue ?? DEFAULT_LINE_PLOT_SETTINGS[defaultSettingType];
};
